import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { PUBLIC } from "@/constants/testIds";
import { useSiteTheme } from "@/components/public/useSiteTheme";
import SeoHead from "@/components/public/SeoHead";
import TurnstileWidget from "@/components/public/TurnstileWidget";

export default function PublicUnsubscribe() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => { publicApi.get(`/public/sites/${slug}/meta`).then(r => setSite(r.data)); }, [slug]);
  useSiteTheme(site);

  async function submit(channel, value) {
    setMsg("");
    if (!value) { setMsg("Please enter your " + (channel === "email" ? "email" : "phone")); return; }
    try {
      await publicApi.post("/public/forms/unsubscribe", {
        site_slug: slug, channel,
        email: channel === "email" ? value : undefined,
        phone: channel === "sms" ? value : undefined,
        reason,
        turnstile_token: turnstileToken || undefined,
      });
      setMsg(`Unsubscribed via ${channel}. We're sorry to see you go.`);
      if (channel === "email") setEmail("");
      else setPhone("");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed.");
    }
  }

  if (!site) return <div className="public-site min-h-screen flex items-center justify-center"><div className="opacity-60 mono">Loading…</div></div>;

  return (
    <div className="public-site">
      <SeoHead site={site} title="Unsubscribe" description="Manage your email and SMS subscriptions." noindex />
      <nav className="border-b" style={{ borderColor: "var(--site-rule)" }}>
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-6 py-3 text-sm">
          <Link to={`/site/${site.slug}`} className="display text-lg font-bold">{site.name}</Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="display text-4xl font-black tracking-tight">Unsubscribe</h1>
        <p className="mt-2 opacity-80">
          You can unsubscribe from email or SMS at any time. This is final and required by law (CCPA, CAN-SPAM).
        </p>

        <div className="mt-8 space-y-6">
          <section className="border p-5" style={{ borderColor: "var(--site-rule)" }}>
            <div className="display text-xl font-bold">Email</div>
            <div className="mt-3 flex">
              <input data-testid={PUBLIC.unsubscribeEmail} type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 bg-transparent border px-3 py-2 text-sm" style={{ borderColor: "var(--site-rule)" }} />
              <button onClick={() => submit("email", email)} className="px-4 text-sm font-semibold" style={{ background: "var(--site-accent)", color: "#fff" }}>Remove</button>
            </div>
          </section>

          <section className="border p-5" style={{ borderColor: "var(--site-rule)" }}>
            <div className="display text-xl font-bold">SMS / Phone</div>
            <div className="mt-3 flex">
              <input data-testid={PUBLIC.unsubscribePhone} type="tel" placeholder="+1 555 555 0123" value={phone} onChange={(e) => setPhone(e.target.value)} className="flex-1 bg-transparent border px-3 py-2 text-sm" style={{ borderColor: "var(--site-rule)" }} />
              <button data-testid={PUBLIC.unsubscribeSubmit} onClick={() => submit("sms", phone)} className="px-4 text-sm font-semibold" style={{ background: "var(--site-accent)", color: "#fff" }}>Remove</button>
            </div>
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-widest opacity-60 mb-1.5">Reason (optional)</div>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-transparent border px-3 py-2 text-sm" style={{ borderColor: "var(--site-rule)" }} placeholder="Help us improve" />
          </section>

          <TurnstileWidget onToken={setTurnstileToken} />

          {msg && <div className="text-sm opacity-90">{msg}</div>}
        </div>
      </div>
    </div>
  );
}
